import type { DagFilterState } from "../dag-viewer/search";
import type { DagCategoryImportance, DagRecordType } from "../dag-viewer/types";
import type { DagDirection, DagSummaryGroup, DagViewMode } from "../dag-viewer/viewProjections";

/** Fixture sizes currently generated and performance-tested by the Lineage Lab. */
export const LINEAGE_LAB_FIXTURE_SIZES = [20, 1_000, 10_000, 50_000] as const;
export type LineageLabFixtureSize = (typeof LINEAGE_LAB_FIXTURE_SIZES)[number];

/** Viewport IDs shared by the embedded lab and the standalone scale harness. */
export const LINEAGE_LAB_VIEWPORTS = ["fill", "1440x900", "1280x720", "1024x768", "844x390"] as const;
export type LineageLabViewport = (typeof LINEAGE_LAB_VIEWPORTS)[number];
export type LineageLabTheme = "light" | "dark";

export interface LineageLabCategorySelector {
  /** Stable key when the command names one of the canonical lanes. */
  key?: string;
  /** Human label used to resolve data-dependent categories at execution time. */
  label: string;
}

export type LineageLabAgentAction =
  | { type: "set-fixture"; size: LineageLabFixtureSize }
  | { type: "set-viewport"; viewport: LineageLabViewport }
  | { type: "set-theme"; theme: LineageLabTheme }
  | {
      type: "configure-view";
      mode?: DagViewMode;
      direction?: DagDirection;
      depth?: number | null;
      summaryGroup?: DagSummaryGroup;
      compareA?: string;
      compareB?: string;
    }
  | { type: "set-search"; query: string; isolateMatches: boolean }
  | { type: "set-filter"; patch: Partial<DagFilterState> }
  | { type: "reset-filters" }
  | { type: "set-category-labels"; visible: boolean }
  | { type: "set-label-scale"; scale: number }
  | { type: "adjust-label-scale"; delta: number }
  | { type: "set-category-visibility"; category: LineageLabCategorySelector; visible: boolean }
  | { type: "show-all-categories" }
  | { type: "set-category-importance"; category: LineageLabCategorySelector; importance: DagCategoryImportance }
  | { type: "move-category"; category: LineageLabCategorySelector; destination: "up" | "down" | "first" | "last" }
  | { type: "run-benchmark" }
  | { type: "reset-lab" };

export interface LineageLabAgentRequirement {
  type: "compare-anchors" | "selection-anchor" | "clarify-command";
  message: string;
}

export interface LineageLabAgentContext {
  /** Existing comparison anchors, normally read from dagA/dagB. */
  compareA?: string | null;
  compareB?: string | null;
  /** Selected records, ordered most-recent first. */
  selectedNodeIds?: readonly string[];
}

export interface LineageLabCommandPlan {
  source: string;
  normalized: string;
  actions: LineageLabAgentAction[];
  requirements: LineageLabAgentRequirement[];
  /** Non-blocking limits the agent should show alongside its interpretation. */
  caveats: string[];
  /** False means the UI should ask the single requirement before executing. */
  ready: boolean;
  summary: string;
}

const VIEW_PATTERNS: ReadonlyArray<{ mode: DagViewMode; pattern: RegExp }> = [
  { mode: "compare", pattern: /\b(?:compare|comparison|diff|difference|side[- ]by[- ]side)\b/ },
  { mode: "impact", pattern: /\b(?:impact|blast radius|affected|downstream impact)\b/ },
  { mode: "focus", pattern: /\b(?:focus|neighbou?rhood|local context)\b/ },
  { mode: "summary", pattern: /\b(?:summary|grouped overview|rollup)\b/ },
  { mode: "runs", pattern: /\b(?:run matrix|runs matrix|sweep matrix|matrix view)\b/ },
  { mode: "audit", pattern: /\b(?:audit|recorded history|event log)\b/ },
  { mode: "flow", pattern: /\b(?:flow|pipeline|dependency view|dependency graph)\b/ },
  { mode: "chronicle", pattern: /\b(?:chronicle|timeline|all data)\b/ },
];

const CATEGORY_ALIASES: ReadonlyArray<{ pattern: RegExp; selector: LineageLabCategorySelector }> = [
  { pattern: /\boperation runs?\b|\boperations?\b/, selector: { key: "operation:runs", label: "operation runs" } },
  { pattern: /\blifecycle(?: and| ·)? decisions?\b/, selector: { key: "event:lifecycle", label: "lifecycle · decisions" } },
  { pattern: /\bplots?\b/, selector: { label: "plots" } },
  { pattern: /\bgeometry\b/, selector: { label: "geometry" } },
  { pattern: /\bmeshes?\b/, selector: { label: "meshes" } },
  { pattern: /\bimages?(?: and| ·)? renders?\b/, selector: { label: "images · renders" } },
  { pattern: /\bdatasets?(?: and| ·)? results?\b/, selector: { label: "datasets · results" } },
  { pattern: /\binputs?(?: and| ·)? configuration\b/, selector: { label: "inputs · configuration" } },
  { pattern: /\brecords?(?: and| ·)? reports?\b/, selector: { label: "records · reports" } },
  { pattern: /\bartifacts?\b/, selector: { label: "artifacts" } },
];

const RECORD_TYPE_TERMS: ReadonlyArray<{ pattern: RegExp; type: DagRecordType }> = [
  { pattern: /\bartifacts?\b/, type: "artifact" },
  { pattern: /\bversions?\b/, type: "version" },
  { pattern: /\boperations?\b/, type: "operation" },
  { pattern: /\bhuman(?: records?)?\b/, type: "human" },
  { pattern: /\bdecisions?\b/, type: "decision" },
  { pattern: /\blifecycle(?: records?)?\b/, type: "lifecycle" },
];

function normalizeCommand(command: string): string {
  return command
    .normalize("NFKC")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleLowerCase();
}

function fixtureSize(command: string): LineageLabFixtureSize | null {
  if (/\b(?:fifty thousand|50\s*k|50[ ,]?000)\b/.test(command)) return 50_000;
  if (/\b(?:ten thousand|10\s*k|10[ ,]?000)\b/.test(command)) return 10_000;
  if (/\b(?:one thousand|1\s*k|1[ ,]?000)\b/.test(command)) return 1_000;
  if (/\b(?:twenty|20)(?:\s+(?:record|node)s?)?\b/.test(command)) return 20;
  return null;
}

function viewport(command: string): LineageLabViewport | null {
  if (/\b(?:phone|mobile|handset|844\s*[x×]\s*390)\b/.test(command)) return "844x390";
  if (/\b(?:tablet|ipad|1024\s*[x×]\s*768)\b/.test(command)) return "1024x768";
  if (/\b(?:laptop|notebook|1280\s*[x×]\s*720)\b/.test(command)) return "1280x720";
  if (/\b(?:desktop|widescreen|1440\s*[x×]\s*900)\b/.test(command)) return "1440x900";
  if (/\b(?:fill frame|fill viewport|full frame)\b/.test(command)) return "fill";
  return null;
}

function requestedView(command: string): DagViewMode | null {
  if (/\bwhat (?:produced|generated|created) this\b/.test(command)) return "focus";
  if (/\bwhat (?:would|could|will) changing this .+? (?:potentially )?(?:affect|impact|change)\b/.test(command)) return "impact";
  return VIEW_PATTERNS.find(({ pattern }) => pattern.test(command))?.mode ?? null;
}

function requestedDirection(command: string): DagDirection | undefined {
  if (/\b(?:both directions?|upstream and downstream|ancestors? and descendants?)\b/.test(command)) return "both";
  if (/\bwhat (?:produced|generated|created) this\b/.test(command)) return "upstream";
  if (/\bwhat (?:would|could|will) changing this .+? (?:potentially )?(?:affect|impact|change)\b/.test(command)) return "downstream";
  if (/\b(?:upstream|ancestors?)\b/.test(command)) return "upstream";
  if (/\b(?:downstream|descendants?)\b/.test(command)) return "downstream";
  return undefined;
}

function requestedDepth(command: string): number | null | undefined {
  if (/\b(?:all (?:hops|depths?)|unlimited depth|every hop)\b/.test(command)) return null;
  const numeric = command.match(/\b([123])\s*(?:hops?|levels?|degrees?)\b/);
  if (numeric) return Number(numeric[1]);
  const words = command.match(/\b(one|two|three)\s+(?:hops?|levels?|degrees?)\b/);
  if (!words) return undefined;
  return ({ one: 1, two: 2, three: 3 } as const)[words[1] as "one" | "two" | "three"];
}

function requestedSummaryGroup(command: string): DagSummaryGroup | undefined {
  const match = command.match(/\b(?:group|summary)\s+by\s+(category|kind|type|state|actor)\b/)
    ?? command.match(/\bby\s+(category|kind|type|state|actor)\b/);
  return match?.[1] as DagSummaryGroup | undefined;
}

function comparisonAnchors(command: string): { a: string; b: string } | null {
  const quoted = command.match(/\bcompare\s+"([^"]+)"\s+(?:with|and|to|vs\.?)\s+"([^"]+)"/);
  if (quoted) return { a: quoted[1]!.trim(), b: quoted[2]!.trim() };
  const ids = command.match(/\bcompare\s+(?:id:)?([a-z0-9][a-z0-9_.:-]*)\s+(?:with|and|to|vs\.?)\s+(?:id:)?([a-z0-9][a-z0-9_.:-]*)\b/);
  return ids ? { a: ids[1]!, b: ids[2]! } : null;
}

function categorySelector(text: string): LineageLabCategorySelector {
  const normalized = text.replace(/\b(?:the|lane|category)\b/g, " ").replace(/\s+/g, " ").trim();
  return CATEGORY_ALIASES.find(({ pattern }) => pattern.test(normalized))?.selector ?? { label: normalized };
}

function categoryActions(command: string): LineageLabAgentAction[] {
  const actions: LineageLabAgentAction[] = [];
  const categoryLabels = /\b(?:category|lane|left[- ]side) labels?\b/;
  if (new RegExp(`\\bhide\\s+(?:the\\s+)?${categoryLabels.source}`).test(command)) {
    actions.push({ type: "set-category-labels", visible: false });
  } else if (new RegExp(`\\bshow\\s+(?:the\\s+)?${categoryLabels.source}`).test(command)) {
    actions.push({ type: "set-category-labels", visible: true });
  }

  const exactScale = command.match(/\b(?:labels?|graph text|text)\s+(?:to|at|=)?\s*(80|90|100|110|120|130|140|150|160)\s*%/);
  if (exactScale) {
    actions.push({ type: "set-label-scale", scale: Number(exactScale[1]) / 100 });
  } else if (/\b(?:(?:make|set)\s+(?:the\s+)?(?:labels?|graph text|text)\s+(?:bigger|larger)|(?:increase|enlarge)\s+(?:the\s+)?(?:labels?|graph text|text))\b/.test(command)) {
    actions.push({ type: "adjust-label-scale", delta: 0.2 });
  } else if (/\b(?:(?:make|set)\s+(?:the\s+)?(?:labels?|graph text|text)\s+smaller|(?:decrease|shrink)\s+(?:the\s+)?(?:labels?|graph text|text))\b/.test(command)) {
    actions.push({ type: "adjust-label-scale", delta: -0.2 });
  }

  if (/\bshow all (?:categories|lanes)\b/.test(command)) actions.push({ type: "show-all-categories" });

  const directVisibility = command.match(/\b(hide|show)\s+(?:the\s+)?(operation runs?|operations?)\b/);
  const namedVisibility = command.match(/\b(hide|show)\s+(?:the\s+)?(.+?)\s+(?:lane|category)\b/);
  const visibility = directVisibility ?? namedVisibility;
  if (visibility && !categoryLabels.test(visibility[0]!)) {
    actions.push({
      type: "set-category-visibility",
      category: categorySelector(visibility[2]!),
      visible: visibility[1] === "show",
    });
  }

  const importance = command.match(/\b(?:make|mark|set)\s+(?:the\s+)?(.+?)\s+(?:as\s+)?(key|important|quiet|normal|standard)\b/);
  if (importance && !/\b(?:labels?|graph text|text)\b/.test(importance[1]!)) {
    const value = importance[2] === "key" || importance[2] === "important"
      ? "key"
      : importance[2] === "quiet"
        ? "quiet"
        : "standard";
    actions.push({ type: "set-category-importance", category: categorySelector(importance[1]!), importance: value });
  }

  const move = command.match(/\bmove\s+(?:the\s+)?(.+?)\s+(up|down|to (?:the )?top|to (?:the )?bottom|first|last)\b/);
  if (move) {
    const rawDestination = move[2]!;
    const destination = rawDestination === "up" || rawDestination === "down"
      ? rawDestination
      : /top|first/.test(rawDestination)
        ? "first"
        : "last";
    actions.push({ type: "move-category", category: categorySelector(move[1]!), destination });
  }
  return actions;
}

function searchAndFilterActions(command: string): LineageLabAgentAction[] {
  if (/\b(?:clear|reset)\s+(?:the\s+)?search\b/.test(command)) {
    return [{ type: "set-search", query: "", isolateMatches: false }];
  }
  if (/\b(?:clear|reset)\s+(?:all\s+)?filters?\b/.test(command)) return [{ type: "reset-filters" }];
  if (/\bshow (?:me )?(?:everything|all records)\b/.test(command)) {
    return [
      { type: "set-search", query: "", isolateMatches: false },
      { type: "reset-filters" },
      { type: "show-all-categories" },
    ];
  }

  const actions: LineageLabAgentAction[] = [];
  const filterPatch: Partial<DagFilterState> = {};
  const structuredTokens = [...command.matchAll(/\b(?:label|name|kind|type|state|status|channel|path|id|digest|hash|anchor|after|before):(?:"[^"]+"|[^\s,;]+)/g)]
    .map((match) => match[0]!.replace(/[.!?]+$/, ""));
  if (structuredTokens.length > 0) {
    actions.push({ type: "set-search", query: structuredTokens.join(" "), isolateMatches: /\b(?:show|only|isolate)\b/.test(command) });
  }

  if (structuredTokens.length === 0 && /\b(?:show|find|isolate)\s+(?:me\s+)?(?:only\s+)?(?:the\s+)?(?:invalidated|invalid|problem|failed)\s*(?:records?|nodes?)?\b/.test(command)) {
    actions.push({ type: "set-search", query: "state:invalidated", isolateMatches: true });
  }

  if (/\bhuman decisions?\b/.test(command)) {
    filterPatch.recordTypes = ["decision"];
    filterPatch.pathStates = ["human"];
  }

  const onlyType = command.match(/\bshow\s+(?:me\s+)?only\s+(?:the\s+)?(.+?)\s*(?:records?|nodes?)?$/);
  if (onlyType) {
    const recordType = RECORD_TYPE_TERMS.find(({ pattern }) => pattern.test(onlyType[1]!))?.type;
    if (recordType) filterPatch.recordTypes = [recordType];
  }

  const topology = command.match(/\bshow\s+(?:me\s+)?only\s+(?:the\s+)?(roots?|leaves|leaf nodes?|anchors?)\b/);
  if (topology) {
    const value = topology[1]!.startsWith("root") ? "root" : topology[1]!.startsWith("anchor") ? "anchor" : "leaf";
    filterPatch.topology = [value];
  }

  const orphanGapReview = /\borphan(?:ed)?\b/.test(command) && /\b(?:gaps?|missing|suspicious)\b/.test(command);
  if (orphanGapReview) {
    // Roots and leaves are observable topology. They are review candidates,
    // not proof that a record is orphaned or that provenance is missing.
    filterPatch.topology = ["root", "leaf"];
  }

  const timeWindow = command.match(/\b(?:last|past)\s+(7|30|90)\s+days?\b/);
  if (timeWindow) {
    filterPatch.timeWindowDays = Number(timeWindow[1]) as 7 | 30 | 90;
  }

  const generic = command.match(/\b(?:find|search(?:\s+for)?|look\s+for)\s+(.+)$/);
  if (generic && structuredTokens.length === 0 && actions.length === 0 && !orphanGapReview && !/\bcompare\b/.test(command)) {
    const query = generic[1]!
      .replace(/\s+in\s+(?:the\s+)?(?:flow|chronicle|focus|impact|summary|audit)(?:\s+view)?\b.*$/, "")
      .replace(/\s+on\s+(?:a\s+)?(?:phone|mobile|tablet|laptop|desktop)\b.*$/, "")
      .trim();
    if (query) actions.push({ type: "set-search", query, isolateMatches: /\bonly\b/.test(command) });
  }
  if (Object.keys(filterPatch).length > 0) actions.push({ type: "set-filter", patch: filterPatch });
  return actions;
}

function describeAction(action: LineageLabAgentAction): string {
  switch (action.type) {
    case "set-fixture": return `load ${action.size.toLocaleString("en-US")} records`;
    case "set-viewport": return `use ${action.viewport === "844x390" ? "the phone frame" : action.viewport}`;
    case "set-theme": return `switch to ${action.theme} mode`;
    case "configure-view": return action.mode ? `open ${action.mode}` : "update view context";
    case "set-search": return action.query ? `search for ${action.query}` : "clear search";
    case "set-filter": return "apply filters";
    case "reset-filters": return "clear filters";
    case "set-category-labels": return `${action.visible ? "show" : "hide"} category labels`;
    case "set-label-scale": return `set labels to ${Math.round(action.scale * 100)}%`;
    case "adjust-label-scale": return `make labels ${action.delta > 0 ? "bigger" : "smaller"}`;
    case "set-category-visibility": return `${action.visible ? "show" : "hide"} ${action.category.label}`;
    case "show-all-categories": return "show all categories";
    case "set-category-importance": return `make ${action.category.label} ${action.importance}`;
    case "move-category": return `move ${action.category.label} ${action.destination}`;
    case "run-benchmark": return "run the performance benchmark";
    case "reset-lab": return "reset the lab";
  }
}

/**
 * Deterministic command planner for the local Lineage Lab agent bar.
 *
 * It deliberately returns serializable intents rather than touching React or
 * browser globals. The UI remains responsible for resolving data-dependent
 * category labels and asking for missing comparison anchors.
 */
export function planLineageLabCommand(
  source: string,
  context: LineageLabAgentContext = {},
): LineageLabCommandPlan {
  const normalized = normalizeCommand(source);
  const actions: LineageLabAgentAction[] = [];
  const requirements: LineageLabAgentRequirement[] = [];
  const caveats: string[] = [];

  if (/^(?:reset|reset lab|reset testbed|start over)$/.test(normalized)) {
    actions.push({ type: "reset-lab" });
  } else {
    const size = fixtureSize(normalized);
    if (size != null) actions.push({ type: "set-fixture", size });

    const requestedViewport = viewport(normalized);
    if (requestedViewport) actions.push({ type: "set-viewport", viewport: requestedViewport });

    if (/\bdark(?: mode| theme)?\b/.test(normalized)) actions.push({ type: "set-theme", theme: "dark" });
    else if (/\blight(?: mode| theme)?\b/.test(normalized)) actions.push({ type: "set-theme", theme: "light" });

    const mode = requestedView(normalized);
    const direction = requestedDirection(normalized);
    const depth = requestedDepth(normalized);
    const summaryGroup = requestedSummaryGroup(normalized);
    if (mode || direction || depth !== undefined || summaryGroup) {
      const viewAction: Extract<LineageLabAgentAction, { type: "configure-view" }> = { type: "configure-view" };
      if (mode) viewAction.mode = mode;
      if (direction) viewAction.direction = direction;
      if (depth !== undefined) viewAction.depth = depth;
      if (summaryGroup) viewAction.summaryGroup = summaryGroup;

      if (mode === "compare") {
        const explicit = comparisonAnchors(normalized);
        const selected = context.selectedNodeIds?.filter(Boolean) ?? [];
        const compareA = explicit?.a ?? context.compareA ?? selected[0] ?? null;
        const compareB = explicit?.b ?? context.compareB ?? selected.find((id) => id !== compareA) ?? null;
        if (compareA && compareB) {
          viewAction.compareA = compareA;
          viewAction.compareB = compareB;
        } else {
          requirements.push({
            type: "compare-anchors",
            message: "Choose the two records or branch tips to compare.",
          });
        }
      }
      if ((mode === "focus" || mode === "impact") && /\bthis\s+(?:plot|mesh|record|node|artifact|version|run|result)\b/.test(normalized)) {
        const selected = context.selectedNodeIds?.find(Boolean);
        if (!selected) {
          requirements.push({
            type: "selection-anchor",
            message: `Select the ${mode === "focus" ? "record to trace upstream" : "record to assess downstream"} first.`,
          });
        }
      }
      actions.push(viewAction);
    }

    actions.push(...searchAndFilterActions(normalized));
    actions.push(...categoryActions(normalized));

    if (/\borphan(?:ed)?\b/.test(normalized) && /\b(?:gaps?|missing|suspicious)\b/.test(normalized)) {
      caveats.push("Showing root and leaf records as review candidates; topology alone cannot prove orphanhood, a missing record, or causality.");
    }

    if (/\b(?:stress test|benchmark|performance test|test performance)\b/.test(normalized)) {
      actions.push({ type: "run-benchmark" });
    }
  }

  if (actions.length === 0) {
    requirements.push({
      type: "clarify-command",
      message: "Try a fixture, view, device, search, filter, category, theme, or benchmark command.",
    });
  }

  const summary = actions.length > 0
    ? actions.map(describeAction).join(" · ")
    : "No Lineage Lab action recognised";
  return {
    source,
    normalized,
    actions,
    requirements,
    caveats,
    ready: requirements.length === 0,
    summary,
  };
}

/**
 * Project URL-backed actions onto the embedded Thread Lineage Lab query
 * contract. Theme is intentionally not projected: the embedded lab executes
 * that action through Thread's global ThemeContext. Search, filters, categories
 * and benchmark actions remain explicit UI intents because DagViewer currently
 * owns those states in React.
 */
export function applyLineageLabPlanToSearch(
  search: string | URLSearchParams,
  plan: Pick<LineageLabCommandPlan, "actions">,
): URLSearchParams {
  const params = new URLSearchParams(typeof search === "string" ? search : search.toString());
  for (const action of plan.actions) {
    switch (action.type) {
      case "set-fixture":
        params.set("labSize", String(action.size));
        break;
      case "set-viewport":
        params.set("labViewport", action.viewport);
        break;
      case "configure-view":
        if (action.mode) params.set("dagView", action.mode);
        if (action.direction) params.set("dagDir", action.direction);
        if (action.depth !== undefined) params.set("dagDepth", action.depth == null ? "all" : String(action.depth));
        if (action.summaryGroup) params.set("dagGroup", action.summaryGroup);
        if (action.compareA) params.set("dagA", action.compareA);
        if (action.compareB) params.set("dagB", action.compareB);
        break;
      case "reset-lab":
        for (const key of ["labSize", "labViewport", "dagView", "dagDir", "dagDepth", "dagGroup", "dagA", "dagB"]) {
          params.delete(key);
        }
        break;
      default:
        break;
    }
  }
  return params;
}
